import React, { useCallback, useRef, RefObject } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import PageLoadingSpinner from '_components/PageLoadingSpinner/PageLoadingSpinner';
const DefaultFallback = React.lazy(() => import('_boundaries/DefaultFallback/DefaultFallback'));
const LanguageFormContainer = React.lazy(() => import('Login/containers/LanguageFormContainer'));

import { LoginFormRenderer } from 'Login/components/LoginForm/LoginFormRenderer';

import { customIdFieldAtom, loginCodeAtom, loginRequestAtom, loginStatusAtom } from '_atoms/auth';
import { languageIdAtom } from '_atoms/language';
import { loginActionAtom } from '_atoms/loginAction';
import { checkForErrorMessage } from '_helpers/loginHelpers';
import { getCookie, retrieveSessionFromStorage } from '_helpers/queryInterceptorHelpers';

import type { CustomIdField, LoginCode, LoginRequest, LoginStatus, Session } from '@/types/auth';

const authenticatedStatusCodes = new Set(['AC', 'AP', 'AV']);

/**
 * Page component responsible for rendering login-related flows before the respondent receives a valid JWT
 * authentication token.
 */
const Login = () => {
  const customIdField: CustomIdField = useAtomValue(customIdFieldAtom);
  const loginCode: LoginCode = useAtomValue(loginCodeAtom);
  const [loginStatus, setLoginStatus] = useAtom(loginStatusAtom);
  const [loginRequest, setLoginRequest] = useAtom(loginRequestAtom);
  const languageId: string | null = useAtomValue(languageIdAtom);
  const login = useSetAtom(loginActionAtom);

  const session: Session | null = retrieveSessionFromStorage();
  const cookieLoginId: string = getCookie('loginid');

  const searchParams: URLSearchParams = useSearchParams()[0];
  const hasError: boolean = checkForErrorMessage(loginStatus);

  // Ensure the search params are captured only once on initial render
  const refSearchParams: RefObject<URLSearchParams> = useRef(searchParams);
  const navigate = useNavigate();

  /**
   * Derive the current login status code:
   * - If the page is first loading, and we have enough info to attempt a login, set the status to "PL" (page loading).
   * - Otherwise, use the status from the loginStatus atom.
   */
  const status: string = (
    !loginCode.id &&
    !loginStatus.previousStatusCodes.length &&
    (session?.id || cookieLoginId || refSearchParams.current?.get('loginCode'))
  )
    ? 'PL'
    : loginStatus.currentStatusCode;

  /**
   * Handle input changes:
   * - Update the LoginRequest atom with the new field value.
   * - Clear any visible error message in the loginStatus atom.
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setLoginRequest((prev: LoginRequest): LoginRequest => ({...prev, [event.target.name]: event.target.value}));
      setLoginStatus((prev: LoginStatus): LoginStatus => ({...prev, errorMessage: ''}));
    },
    [setLoginRequest, setLoginStatus]
  );

  /**
   * Login and navigate to the welcome page.
   */
  const handleSubmit: () => void = useCallback(
    async (): Promise<void> => {
      await login(null);
      navigate('/welcome');
    },
    [login, navigate]
  );

  // When already logged in, navigate to the overview page.
  if (authenticatedStatusCodes.has(status)) return <Navigate to={`/overview`} replace={true}/>

  // If multiple languages are available and none is selected, show the language picker
  if (loginCode?.languages?.length > 1 && !languageId) {
    return (
      <ErrorBoundary FallbackComponent={DefaultFallback}>
        <React.Suspense fallback={<PageLoadingSpinner label="Loading language options" />}>
          <LanguageFormContainer />
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  // Render the appropriate login form or spinner based on the current login status
  return (
    <ErrorBoundary FallbackComponent={DefaultFallback}>
      <React.Suspense fallback={<PageLoadingSpinner label="Logging in" />}>
        <LoginFormRenderer
          currentStatusCode={status}
          customIdField={customIdField ?? loginCode?.customIdField ?? null}
          hasError={hasError}
          loginRequest={loginRequest}
          onChange={handleChange}
          onSubmit={handleSubmit}
        />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default Login;